import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { focusElement } from 'platform/utilities/ui';
import { recordDatalayerEvent } from '../../utilities/analytics';
import useClickOutside from '../../hooks/useClickOutside';
import ToggleWrapper from './ToggleWrapper';

const NavSection = ({ link }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const isActive = path => window.location.pathname.startsWith(path);

  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  useClickOutside(containerRef, isOpen, () => setIsOpen(false));

  const handleEscape = event => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      focusElement(buttonRef?.current);
    }
  };

  const handleBlur = event => {
    if (!containerRef.current.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div ref={containerRef} onKeyDown={handleEscape} onBlur={handleBlur}>
      {/* eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component */}
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={`nav-section-${link.TEST_ID}`}
        className={`nav__btn is--section ${
          isOpen ? 'is--expanded' : ''
        } desktop ${isActive(link.URL) && 'is--active'}`}
        onClick={toggleOpen}
      >
        {link.LABEL}
        <va-icon
          size="3"
          icon="chevron_right"
          class="vads-u-color--white vads-u-margin-left--0p5"
        />
      </button>
      <ul
        id={`nav-section-${link.TEST_ID}`}
        className={`nav__section vads-u-margin--0 ${
          isOpen ? '' : 'vads-u-display--none'
        }`}
      >
        {link.SECTIONS.map((section, j) => (
          <ToggleWrapper
            toggleKey={section?.FEATURE_FLAG_NAME}
            displayWhenDisabled={section?.DISPLAY_WHEN_DISABLED}
            key={`nav-subsection-${j}`}
          >
            <li className="vads-u-margin-bottom--3">
              {j !== 0 && (
                <hr
                  aria-hidden="true"
                  className="vads-u-margin-y--2 nav__section-divider"
                />
              )}
              {'LABEL' in section && (
                <h4 className="vads-u-font-family--sans">{section.LABEL}</h4>
              )}
              <ul>
                {section.LINKS.map((subLink, k) => (
                  <ToggleWrapper
                    toggleKey={subLink.FEATURE_FLAG_NAME}
                    displayWhenDisabled={subLink.DISPLAY_WHEN_DISABLED}
                    key={`nav-sublink-${k}`}
                  >
                    <li>
                      <a
                        href={subLink.URL}
                        data-testid={subLink.TEST_ID}
                        onClick={recordDatalayerEvent}
                        data-eventname="nav-link-click"
                      >
                        {subLink.LABEL}
                      </a>
                    </li>
                  </ToggleWrapper>
                ))}
              </ul>
            </li>
          </ToggleWrapper>
        ))}
      </ul>
    </div>
  );
};

NavSection.propTypes = {
  link: PropTypes.shape({
    LABEL: PropTypes.string.isRequired,
    URL: PropTypes.string.isRequired,
    TEST_ID: PropTypes.string.isRequired,
    SECTIONS: PropTypes.arrayOf(
      PropTypes.shape({
        LABEL: PropTypes.string,
        LINKS: PropTypes.arrayOf(
          PropTypes.shape({
            LABEL: PropTypes.string.isRequired,
            URL: PropTypes.string.isRequired,
            TEST_ID: PropTypes.string.isRequired,
            FEATURE_FLAG_NAME: PropTypes.string,
          }),
        ).isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

export default NavSection;
